import React, { createContext, useContext, useEffect, useState, useMemo  } from "react";
import { useParams, useSearchParams } from "react-router-dom";

type ViewMode = "list" | "detail";

interface ValidationContextType {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  meterRunId: string | undefined;
  readingId: string | null;
  cycleId: any | null;
  validatorId;
  setValidatorId: (validatorId: any[]) => void;
  codeId: number | null;
  setCodeId: (codeId: number | null) => void;
  nextReadingId: number | null;
  setNextReadingId: (nextReadingId: number) => void;
  goToNextReading: () => void;
  duplicateReadingId: string | null;
}

const ValidationContext = createContext<ValidationContextType | undefined>(
  undefined
);

export const ValidationContextProvider = ({
  children,
  cycleId,
}: {
  children: React.ReactNode;
  cycleId: number | null;
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>("detail");
  const [validatorId, setValidatorId] = useState<string[] | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [codeId, setCodeId] = useState<number | null>(null);
  const [nextReadingId, setNextReadingId] = useState<number | null>(null);
  const { meterRunId } = useParams();
  const readingId = searchParams.get("readingId");
  const tab = searchParams.get("tab");
  const duplicateReadingId = searchParams.get("duplicateReadingId");

  // ✅ Extract validatorIds from URL if present
  const validatorIdsFromUrl = useMemo(() => {
    const idsParam = searchParams.get("validatorIds");
    return idsParam ? idsParam.split(",").filter(Boolean) : null;
  }, [searchParams]);

   // ✅ Initialize validatorId state from URL when available
  useEffect(() => {
    if (validatorIdsFromUrl?.length) {
      setValidatorId(validatorIdsFromUrl);
    }
  }, [validatorIdsFromUrl]);
  
  useEffect(() => {
    if (!readingId) {
      setViewMode("list");
    } else {
      setViewMode("detail");
    }
  }, [readingId]);
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    // params.delete("readingId");
    params.delete("duplicateReadingId");
    setSearchParams(params);
  }, [tab]);

  const goToNextReading = () => {
    if (nextReadingId !== null) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("readingId", nextReadingId.toString());
      setSearchParams(updatedParams);
    }
    if (!nextReadingId) {
      setViewMode("list");
    }
  };

  return (
    <ValidationContext.Provider
      value={{
        viewMode,
        setViewMode,
        meterRunId,
        readingId,
        cycleId,
        validatorId,
        setValidatorId,
        codeId,
        setCodeId,
        nextReadingId,
        setNextReadingId,
        goToNextReading,
        duplicateReadingId,
      }}
    >
      {children}
    </ValidationContext.Provider>
  );
};

export const useValidationContext = () => {
  const context = useContext(ValidationContext);
  if (!context) {
    throw new Error(
      "useValidationContext must be used inside ValidationContextProvider"
    );
  }
  return context;
};
